import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';

const OrganizationContext = createContext();

export const OrganizationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize organization state from storage
  useEffect(() => {
    const initializeOrganization = () => {
      try {
        // First check sessionStorage for the most recent selection
        const currentSelectedOrgData = sessionStorage.getItem('currentSelectedOrganization');
        if (currentSelectedOrgData) {
          const currentSelectedOrg = JSON.parse(currentSelectedOrgData);
          if (currentSelectedOrg) {
            setCurrentOrganization(currentSelectedOrg);
            return;
          }
        }
        
        // If not found in sessionStorage, check localStorage
        const dashboardState = localStorage.getItem('dashboardState');
        if (dashboardState) {
          const parsedState = JSON.parse(dashboardState);
          if (parsedState.selectedOrganization) {
            setCurrentOrganization(parsedState.selectedOrganization);
            return;
          }
        }
      } catch (error) {
        console.error('Error initializing organization from storage:', error);
      }
    };

    initializeOrganization();
  }, []);

  // Fetch organizations when user changes
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        if (user.isMultiOrg) {
          // For multi-org users, fetch all their organizations
          const res = await axios.get('/api/organizations/user');
          setOrganizations(res.data);
          
          // If we don't have a current organization yet, set the first one
          if (!currentOrganization && res.data.length > 0) {
            setCurrentOrganization(res.data[0]);
            // Also store in sessionStorage for persistence
            sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(res.data[0]));
          }
        } else {
          // For single-org users, fetch their organization
          const res = await axios.get('/api/organizations/current');
          if (res.data) {
            setOrganizations([res.data]);
            
            // If we don't have a current organization yet, set this one
            if (!currentOrganization) {
              setCurrentOrganization(res.data);
              // Also store in sessionStorage for persistence
              sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(res.data));
            }
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setError('Failed to load organizations');
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [user, currentOrganization]);

  // Function to update the current organization
  const updateCurrentOrganization = (organization) => {
    setCurrentOrganization(organization);
    
    // Store in sessionStorage for persistence
    sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(organization));
    
    // Also update dashboardState in localStorage for backward compatibility
    try {
      const dashboardState = localStorage.getItem('dashboardState');
      if (dashboardState) {
        const parsedState = JSON.parse(dashboardState);
        parsedState.selectedOrganization = organization;
        localStorage.setItem('dashboardState', JSON.stringify(parsedState));
      } else {
        localStorage.setItem('dashboardState', JSON.stringify({ selectedOrganization: organization }));
      }
    } catch (error) {
      console.error('Error updating dashboardState:', error);
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        loading,
        error,
        updateCurrentOrganization
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
